import React, { useState, useRef, useCallback } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RecipePrint from './RecipePrint';

// A4 at 96dpi
const PAGE_WIDTH_PX = 794;
const MIN_SCALE = 0.3;
const MAX_SCALE = 1.0;
const SCALE_STEP = 0.1;

const ExportOptionsDialog = ({ open, onClose, onExport, recipe, multiplier = 1 }) => {
    const [includePhoto, setIncludePhoto] = useState(true);
    const [singleColumn, setSingleColumn] = useState(false);
    const [fontSize, setFontSize] = useState('normal');
    const [previewScale, setPreviewScale] = useState(0.62);

    const wheelCleanup = useRef(null);
    const lastTouchDist = useRef(null);
    const dragState = useRef(null);
    const scrollRef = useRef(null);

    const previewRef = useCallback((el) => {
        if (wheelCleanup.current) { wheelCleanup.current(); wheelCleanup.current = null; }
        scrollRef.current = el;
        if (!el) return;
        const onWheel = (e) => {
            if (!e.ctrlKey) return;
            e.preventDefault();
            setPreviewScale(s => Math.min(MAX_SCALE, Math.max(MIN_SCALE, parseFloat((s - e.deltaY * 0.005).toFixed(2)))));
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        wheelCleanup.current = () => el.removeEventListener('wheel', onWheel);
    }, []);

    const handleTouchStart = (e) => {
        if (e.touches.length === 2) {
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            lastTouchDist.current = Math.hypot(dx, dy);
        }
    };

    const handleTouchMove = (e) => {
        if (e.touches.length !== 2 || !lastTouchDist.current) return;
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.hypot(dx, dy);
        const delta = (dist - lastTouchDist.current) * 0.004;
        lastTouchDist.current = dist;
        setPreviewScale(s => Math.min(MAX_SCALE, Math.max(MIN_SCALE, parseFloat((s + delta).toFixed(2)))));
    };

    const handleMouseDown = (e) => {
        e.preventDefault();
        const el = scrollRef.current;
        if (!el) return;
        dragState.current = { startX: e.clientX, startY: e.clientY, scrollLeft: el.scrollLeft, scrollTop: el.scrollTop };
        el.style.cursor = 'grabbing';
        el.style.userSelect = 'none';
    };

    const handleMouseMove = (e) => {
        if (!dragState.current) return;
        const el = scrollRef.current;
        if (!el) return;
        el.scrollLeft = dragState.current.scrollLeft - (e.clientX - dragState.current.startX);
        el.scrollTop = dragState.current.scrollTop - (e.clientY - dragState.current.startY);
    };

    const handleMouseUp = () => {
        dragState.current = null;
        if (scrollRef.current) { scrollRef.current.style.cursor = 'grab'; scrollRef.current.style.userSelect = ''; }
    };

    const handleExport = () => {
        onExport({ includePhoto, singleColumn, fontSize });
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: { xs: 'auto', sm: '90vh' } } }}>
            <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><PrintIcon sx={{ color: 'primary.main' }} />Export Options</DialogTitle>
            <Divider />
            <DialogContent sx={{ display: 'flex', gap: 3, pt: 2, pb: 2, flex: 1, minHeight: 0 }}>
                {/* Controls */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 220 }}>
                    <FormControlLabel
                        control={<Switch checked={includePhoto} onChange={e => setIncludePhoto(e.target.checked)} />}
                        label={
                            <Box>
                                <Typography variant="body2" fontWeight={600}>Include photo</Typography>
                                <Typography variant="caption" color="text.secondary">Turn off to save ink</Typography>
                            </Box>
                        }
                    />
                    <FormControlLabel
                        control={<Switch checked={singleColumn} onChange={e => setSingleColumn(e.target.checked)} />}
                        label={
                            <Box>
                                <Typography variant="body2" fontWeight={600}>Single column</Typography>
                                <Typography variant="caption" color="text.secondary">Ingredients and directions stacked</Typography>
                            </Box>
                        }
                    />
                    <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Text size</Typography>
                        <ToggleButtonGroup
                            value={fontSize}
                            exclusive
                            onChange={(_, val) => { if (val) setFontSize(val); }}
                            size="small"
                        >
                            <ToggleButton value="small">Small</ToggleButton>
                            <ToggleButton value="normal">Normal</ToggleButton>
                            <ToggleButton value="large">Large</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>

                {/* Mini preview — hidden on mobile */}
                <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">Preview</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title="Zoom out">
                                <span>
                                    <IconButton size="small" onClick={() => setPreviewScale(s => Math.max(MIN_SCALE, +(s - SCALE_STEP).toFixed(1)))} disabled={previewScale <= MIN_SCALE}>
                                        <ZoomOutIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'center' }}>
                                {Math.round(previewScale * 100)}%
                            </Typography>
                            <Tooltip title="Zoom in">
                                <span>
                                    <IconButton size="small" onClick={() => setPreviewScale(s => Math.min(MAX_SCALE, +(s + SCALE_STEP).toFixed(1)))} disabled={previewScale >= MAX_SCALE}>
                                        <ZoomInIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Box
                        ref={previewRef}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        sx={{ flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'auto', background: '#f5f5f5', cursor: 'grab', display: 'flex', justifyContent: 'center' }}
                    >
                        <Box sx={{ zoom: previewScale, width: PAGE_WIDTH_PX, minHeight: 1123, flexShrink: 0, background: '#fff', px: '64pt', pt: '36pt', pb: '48pt', boxSizing: 'border-box', pointerEvents: 'none', alignSelf: 'flex-start' }}>
                            <RecipePrint
                                recipe={recipe}
                                multiplier={multiplier}
                                includePhoto={includePhoto}
                                singleColumn={singleColumn}
                                fontSize={fontSize}
                                preview
                            />
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button onClick={handleExport} variant="contained">Print / Save PDF</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExportOptionsDialog;
